import {
  addDays,
  addMinutes,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  formatISO,
  isWithinInterval,
  parse,
  parseISO,
  set,
  subMinutes,
} from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';

export const formatDate = (date: string | number | Date): string => {
  try {
    return format(parseISO(String(date)), 'dd/MM/yyyy', {
      locale: ptBR,
    });
  } catch (e) {
    return String(date);
  }
};

export const formatDateTime = (date: string | number | Date): string => {
  try {
    return format(parseISO(String(date)), 'dd/MM/yyyy HH:mm', {
      locale: ptBR,
    });
  } catch (e) {
    return String(date);
  }
};

export const formatDateTimeFromTimestamp = (date: string | number | Date): string => {
  try {
    const dateParsed = new Date(date);

    return format(dateParsed, 'dd/MM/yyyy HH:mm', {
      locale: ptBR,
    });
  } catch (e) {
    console.log(e);
    return String(date);
  }
};

export const formatDateFromTimestamp = (date: string | number | Date): string => {
  try {
    const dateParsed = new Date(date);

    return format(dateParsed, 'dd/MM/yyyy', {
      locale: ptBR,
    });
  } catch (e) {
    console.log(e);
    return String(date);
  }
};

export const formatTimeFromTimestamp = (date: string | number | Date): string => {
  try {
    const dateParsed = new Date(date);

    return format(dateParsed, 'HH:mm', {
      locale: ptBR,
    });
  } catch (e) {
    console.log(e);
    return String(date);
  }
};

export const getDayNameFromTimestamp = (date: string | number | Date): string => {
  try {
    const dateParsed = new Date(date);

    return format(dateParsed, 'EEEE', {
      locale: ptBR,
    });
  } catch (e) {
    console.log(e);
    return String(date);
  }
};

export const dateToUTC = (date: string | number | Date): string => {
  try {
    return formatISO(new Date(date));
  } catch (e) {
    return String(date);
  }
};

export const brDateToUTC = (date: string): string => {
  try {
    const d = date.split('/');
    const newDate = `${d[2]}-${d[1]}-${d[0]}T12:00:00-00:00`;

    return formatISO(new Date(newDate));
  } catch (e) {
    return String(date);
  }
};

export const nowToUTC = (): string => dateToUTC(new Date());

export const getTimeCalculatedFromDate = (pastDate: string): string => {
  const daysPassed = differenceInDays(new Date(), parseISO(pastDate));

  if (daysPassed < 1) {
    const hoursPassed = differenceInHours(new Date(), parseISO(pastDate));

    if (hoursPassed < 1) {
      const minutesPassed = differenceInMinutes(new Date(), parseISO(pastDate));

      if (minutesPassed === 0) return 'menos de 1 minuto';
      return minutesPassed === 1 ? '1 minuto' : `${minutesPassed} minutos`;
    }
    return hoursPassed === 1 ? '1 hora' : `${hoursPassed} horas`;
  }
  return daysPassed === 1 ? '1 dia' : `${daysPassed} dias`;
};

export const isDateInPast = (firstDate: Date, secondDate: Date): boolean =>
  firstDate.setHours(0, 0, 0, 0) < secondDate.setHours(0, 0, 0, 0);

export const brDateToYYYYMMDD = (date: string): string => {
  try {
    const d = date.split('/');
    return `${d[2]}-${d[1]}-${d[0]}`;
  } catch (e) {
    return String(date);
  }
};

export const formatStringToDateTime = {
  date: (date: string): Date | null => {
    if (!date) {
      return null;
    }

    // Timezone differences
    return addMinutes(new Date(date), new Date().getTimezoneOffset());
  },

  time: (time: string): Date | null => {
    if (!time) {
      return null;
    }

    const [hours, minutes] = time.split(':');

    const currentDate = new Date();
    const newDate = set(currentDate, { hours: Number(hours), minutes: Number(minutes) });

    return newDate;
  },
};

export const formatDateWithoutParsing = (date: Date, dateFormat?: string): string =>
  format(date, dateFormat || 'dd/MM/yyyy', {
    locale: ptBR,
  });

export const formatIntervalHours = (horaFinal: string, horaInicial: string): string[] =>
  Array.from(
    {
      length: differenceInHours(
        parse(horaFinal, 'HH:mm', new Date()),
        parse(horaInicial, 'HH:mm', new Date()),
      ),
    },
    (_, index) => {
      const date = parse(horaInicial, 'HH:mm', new Date());
      date.setHours(date.getHours() + index);
      return formatDateWithoutParsing(date, 'HH:mm');
    },
  );

export const formatShortDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    // const weekDay = format(date, 'EEE', { locale: ptBR });
    const day = format(date, 'dd');
    const month = format(date, 'MM');

    return `${day}/${month}`;
  } catch (error) {
    return dateString;
  }
};

export const formatDateTimeLabel = (dateString: string): string => {
  const dateParsed = new Date(dateString);

  const time = format(dateParsed, 'HH:mm', {
    locale: ptBR,
  });

  const date = parseISO(dateString);

  const day = format(date, 'dd');
  const month = format(date, 'MM');

  return `${day}/${month} - ${time.replace(':', 'h')}`;
};

export const checkInterval = (dateString: string): boolean => {
  const providedDate = parse(dateString, 'yyyy-MM-dd HH:mm:ss', new Date());
  const currentDate = new Date();

  const date15MinutesBefore = subMinutes(providedDate, 15);

  const date2DaysAfter = addDays(providedDate, 2);

  const isWithinTimeFrame = isWithinInterval(currentDate, {
    start: date15MinutesBefore,
    end: date2DaysAfter,
  });

  return !isWithinTimeFrame;
};
